import Vue, { DirectiveBinding } from 'vue';

declare global {
  interface HTMLElement {
    focusTrapHandler?: (event: KeyboardEvent) => void;
  }
}

// helper function to get all focusable elements in the element
function getFocusableElements(el: HTMLElement) {
  return el.querySelectorAll(
    'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
  );
}

// v-focus-trap Directive:
// 1. Ensures the attached element is focusable and automatically focuses it (or its first child).
// 2. Traps keyboard navigation (Tab/Shift+Tab) within the element.
// 3. It also calls a function when the user presses the Escape (Esc) key(this can be used to close a modals)
Vue.directive('focus-trap', {
  inserted(el: HTMLElement, binding: DirectiveBinding<() => void>) {
    // To add on component type in attribute - example v-focus-trap="closeModal"
    // binding.value will be that closeModal function

    // checks if element can be focused
    if (!el.hasAttribute('tabindex')) {
      el.setAttribute('tabindex', '0');
    }

    // focus on the element or its first child
    const focusable = getFocusableElements(el);
    if (focusable.length) {
      (focusable[0] as HTMLElement).focus();
    } else {
      el.focus();
    }

    // create a keydown handler for both ESC and tab-trapping
    el.focusTrapHandler = (event: KeyboardEvent) => {
      //if ESC, call the function that was passed (like closeModal)
      if (event.key === 'Escape' || event.key === 'Esc') {
        if (typeof binding.value === 'function') {
          binding.value();
        }
        return;
      }

      //traps focus on Tab / Shift+Tab
      if (event.key === 'Tab') {
        const focusableEls = getFocusableElements(el);
        const firstEl = focusableEls[0] as HTMLElement;
        const lastEl = focusableEls[focusableEls.length - 1] as HTMLElement;

        if (focusableEls.length === 0) return;

        // SHIFT + Tab
        if (event.shiftKey && document.activeElement === firstEl) {
          lastEl.focus();
          event.preventDefault();
        }
        // Tab (without SHIFT)
        else if (!event.shiftKey && document.activeElement === lastEl) {
          firstEl.focus();
          event.preventDefault();
        }
      }
    };

    // attaches that handler
    el.addEventListener('keydown', el.focusTrapHandler);
  },

  unbind(el: HTMLElement) {
    if (el.focusTrapHandler) {
      el.removeEventListener('keydown', el.focusTrapHandler);
    }
    delete el.focusTrapHandler;
  }
});
